// Entry point. Phase A foundation: boot the Fabric canvas, build the (stubbed)
// header + secondary toolbar, init history/keyboard, wire canvas events, install
// the frozen dev/test hooks, then boot. Toolbar UI is filled by ui/toolbar.ts
// (Phase B); panels/status/modals are filled by their own modules at runtime.
import '../styles/main.css'
import { initCanvas, fitToView } from './canvas'
import { getCanvas } from './state'
import { initHistory } from './history'
import { initKeyboard } from './keyboard'
import { enableTableInteraction } from './widgets/table-widget'
import { updateLayers } from './panels/layers'
import { onSelectionChange } from './panels/properties'
import { updateStatus } from './statusbar'
import { initAuth, onAuthChange } from './auth/supabase'
import { buildHeader, buildSecondaryToolbar, refreshHeader, setAlignToolsVisible } from './ui/toolbar'
import { installDevHooks } from './devhooks'
import { isMobile, openMobileEditor } from './mobile'
import { initPages, buildPageTabBar } from './pages'
import { applyProductChrome } from './product'
import { trackEvent } from './analytics'
import { readDeepLink, applySizeDeepLink, applyAuthDeepLink } from './deeplink'
import { fitTextToWidth } from './widgets/autofit'

function boot(): void {
  applyProductChrome() // product title/accent before any UI is built
  initCanvas()

  // Toolbars (header + secondary). Phase B fills these; today they are stubs.
  buildHeader()
  buildSecondaryToolbar()

  initHistory()
  initKeyboard()
  fitToView()

  // Wire canvas events to panels + status bar.
  const canvas = getCanvas()
  const refresh = () => { updateStatus(); updateLayers() }
  canvas.on('object:added', refresh)
  canvas.on('object:removed', refresh)
  enableTableInteraction(canvas) // dblclick-edit / select / resize / keyboard for tables
  const onSelect = () => {
    onSelectionChange()
    updateLayers()
    setAlignToolsVisible(!!canvas.getActiveObject())
  }
  // Auto-shrink to fit width: re-fit while typing and after a box resize.
  const refit = (opt: { target?: unknown }) => {
    const t = opt.target as import('fabric').FabricObject | undefined
    if (t && fitTextToWidth(t)) canvas.requestRenderAll()
  }
  canvas.on('text:changed', refit)
  canvas.on('object:modified', (opt) => { refit(opt); onSelectionChange() })
  canvas.on('selection:created', onSelect)
  canvas.on('selection:updated', onSelect)
  canvas.on('selection:cleared', onSelect)
  updateStatus()
  updateLayers()
  onSelectionChange()

  // Multi-page documents (invoices): seed one page from the canvas + show the tab bar.
  initPages()
  buildPageTabBar()

  // Auth: reflect session in the toolbar + status, then check session.
  onAuthChange(() => { updateStatus(); refreshHeader() })
  void initAuth().then(() => applyAuthDeepLink()) // ?plan= / ?signup / ?login= (signed-out only)

  window.addEventListener('resize', fitToView)

  // Frozen test/debug hook surface (window.__olm). Keep last so it sees a fully
  // wired editor.
  installDevHooks()

  // Persistent in-editor ad placements (anonymous free tier only; see src/ads/).
  void import('./ads/placements').then(({ initAdPlacements }) => initAdPlacements())

  // Deep links from the marketing site (?sheet= / ?size=): a consumed link wins
  // over the autosave restore unless ?continue is present — the click promised
  // the editor at that size; the autosave stays in storage until the first edit.
  const link = readDeepLink()
  const linkConsumed = applySizeDeepLink(link)

  // Crash/refresh recovery: restore first (so boot never saves a blank canvas
  // over stored work), then start autosaving. Reads classic-editor autosaves too.
  void import('./autosave').then(async ({ initAutosave, restoreAutosave }) => {
    if (!linkConsumed || link.continue) await restoreAutosave()
    initAutosave()
  })
  trackEvent('app_loaded', { tool: 'openlabelmaker' })

  // On phones / narrow viewports, show the dedicated mobile flow (it hides #app).
  if (isMobile()) openMobileEditor()
  console.info('[olm-editor] Phase A booted — shell + canvas + history + keyboard ready')
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot)
} else {
  boot()
}
